import { ACTION_ICONS, ROLE_VISIBILITY, isVisibleForRole } from "../../config/siteConfig";
import React, { useContext, useEffect, useState } from 'react'
import DContext from '../../context/DContext';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import DatePicker from 'react-multi-date-picker';
import { Modal } from 'react-bootstrap';
import DetailView from '../location/DetailView';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useLoadingBar } from 'react-top-loading-bar';
import ContentLoader from '../loader/ContentLoader';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { notificationsService } from '../../services/notifications.service';

const MySwal = withReactContent(Swal);

const REFERENCE_TYPES = ["booking", "refund", "wallet", "supplier", "vendor", "inventory", "flight", "user", "report", "other"];
const REFERENCE_OPTIONS = REFERENCE_TYPES.map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
const READ_OPTIONS = [{ value: "false", label: "Unread" }, { value: "true", label: "Read" }];
const STATUS_FILTER_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
];

/* ---------- styles ---------- */
const labelStyle = { fontWeight: 600, fontSize: 12.5, color: "#334155", marginBottom: 5 };
const inputStyle = { borderRadius: 6, border: "1px solid #c3cad6", fontSize: 12, height: 34, background: "#fff" };
const filterInputStyle = { ...inputStyle, background: "#f8f9fe", border: "1px solid #e8edf5", borderRadius: 8 };
const selectStyles = {
  control: (base) => ({
    ...base, borderRadius: 6, border: "1px solid #c3cad6", minHeight: 34, height: 34,
    fontSize: 12, boxShadow: "none", "&:hover": { borderColor: "var(--btn-bg)" }
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 12 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};
const pageBtn = (active, disabled) => ({
  minWidth: 32, height: 32, padding: "0 8px", borderRadius: 8,
  border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
  background: active ? "var(--page-active-bg)" : "#fff",
  color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
  fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer"
});
const chip = (color, bg, border) => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
  borderRadius: 10, background: bg, border: "1px solid " + border, color, fontSize: 13
});
const iconBtn = (bg, color) => ({
  width: 32, height: 32, borderRadius: 8, background: bg, color, border: 'none', padding: 0
});
const outlineBtn = { background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" };
const primaryBtn = { background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 };

const initialForm = { event_type: "", title: "", message: "", user_id: "", reference_id: "", reference_type: "", booking_id: "", amount: "", status: "pending" };
const initialFilters = { event_type: "", reference_type: "", is_read: "", status: "", from: "", to: "" };

const formatDate = (v) => (v ? new Date(v).toLocaleString() : "");

const statusColors = {
  success: { bg: "#d4edda", color: "#155724" },
  pending: { bg: "#fff3cd", color: "#856404" },
  failed: { bg: "#f8d7da", color: "#721c24" },
};

const NotificationList = () => {
  const { start, complete } = useLoadingBar();
  const { authState } = useContext(DContext);
  const show = ROLE_VISIBILITY.notificationsPage;
  const showAddButton = isVisibleForRole(show.addButton, authState);
  const showMarkAllRead = isVisibleForRole(show.markAllReadButton, authState);
  const showMarkRead = isVisibleForRole(show.markReadAction, authState);

  /* ---------- list state ---------- */
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  /* ---------- popup (add / view) state ---------- */
  const [modal, setModal] = useState({ show: false, mode: "add", id: null });
  const [form, setForm] = useState(initialForm);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const totalRecords = pagination?.total || 0;
  const totalPage = pagination?.totalPages || 1;
  const unreadCount = items.filter(i => !i?.is_read).length;
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);
  const isView = modal.mode === "view";

  useEffect(() => {
    loadItems();
  }, [pagingLimit, pageValue, appliedFilters]);

  /* ---------- API: list ---------- */
  const loadItems = async () => {
    try {
      setLoading(true);
      start();
      const query = { page: Number(pageValue), limit: Number(pagingLimit) };
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== "" && value !== null) query[key] = value;
      });
      const res = await notificationsService.list(query);
      if (res?.success) {
        setItems(res.resData?.data || []);
        setPagination(res.resData?.pagination || { total: 0, totalPages: 1 });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
      complete();
    }
  };

  /* ---------- filters ---------- */
  const handleFilterText = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
  const setFilterSelect = (name) => (o) => setFilters({ ...filters, [name]: o ? o.value : "" });

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  /* ---------- pagination ---------- */
  const handlePagingLimit = (e) => { setPageValue(1); setPagingLimit(e.target.value); };
  const handlePagination = (n) => setPageValue(n);
  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) setPageValue(n);
    setGoPage("");
  };
  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter(n => n >= 1 && n <= totalPage).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  /* ---------- popup open / close ---------- */
  const closeModal = () => setModal({ ...modal, show: false });

  const openAdd = () => {
    setForm(initialForm);
    setModal({ show: true, mode: "add", id: null });
  };

  const openView = async (row) => {
    // show the row data immediately, then refresh from the details API
    setModal({ show: true, mode: "view", id: row?.id });
    setDetail(row);
    try {
      setModalLoading(true);
      const res = await notificationsService.details(row?.id);
      if (res?.success) {
        const d = res.resData?.data ?? res.resData?.notification ?? res.resData;
        if (d && typeof d === "object" && !Array.isArray(d)) setDetail({ ...row, ...d });
      }
    } catch (err) {
      // keep the row data already shown
    } finally {
      setModalLoading(false);
    }
  };

  const handleFormText = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  /* ---------- API: create (with confirmation popup) ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.event_type.trim()) { toast.error("Event Type is required"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.message.trim()) { toast.error("Message is required"); return; }
    if (!form.user_id) { toast.error("User ID is required"); return; }
    if (!form.reference_id) { toast.error("Reference ID is required"); return; }
    if (!form.reference_type) { toast.error("Reference Type is required"); return; }

    const confirm = await MySwal.fire({
      title: "Save Notification?",
      text: "Do you want to add this notification?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, save",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);
      const payload = {
        event_type: form.event_type.trim(),
        title: form.title.trim(),
        message: form.message.trim(),
        user_id: Number(form.user_id),
        reference_id: Number(form.reference_id),
        reference_type: form.reference_type,
        metadata: {
          booking_id: form.booking_id.trim(),
          ...(form.amount !== "" ? { amount: Number(form.amount) } : {}),
        },
        status: form.status,
      };
      const res = await notificationsService.create(payload);
      if (res?.success) {
        toast.success(res.message);
        closeModal();
        loadItems();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  /* ---------- API: mark one as read ---------- */
  const handleMarkRead = async (row) => {
    const result = await MySwal.fire({
      title: "Mark as read?",
      text: "Do you want to mark this notification as read?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, mark as read",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await notificationsService.markRead(row?.id);
      if (res?.success) toast.success(res.message); else toast.error(res?.message);
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
      loadItems();
    }
  };

  /* ---------- API: mark all as read (with confirmation) ---------- */
  const handleMarkAllRead = async () => {
    const result = await MySwal.fire({
      title: "Mark all as read?",
      text: "All your notifications will be marked as read.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, mark all",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setMarkingAll(true);
      const res = await notificationsService.markAllRead();
      if (res?.success) toast.success(res.message); else toast.error(res?.message);
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setMarkingAll(false);
      loadItems();
    }
  };

  /* ---------- export ---------- */
  const csvHeaders = [
    { label: "Title", key: "c0" },
    { label: "Message", key: "c1" },
    { label: "Event Type", key: "c2" },
    { label: "Reference Type", key: "c3" },
    { label: "Read", key: "c4" },
    { label: "Status", key: "status" },
    { label: "Created At", key: "c5" },
  ];
  const csvData = items.map(row => ({
    c0: row?.title,
    c1: row?.message,
    c2: row?.event_type,
    c3: row?.reference_type,
    c4: row?.is_read ? "Read" : "Unread",
    status: row?.status,
    c5: formatDate(row?.created_at),
  }));

  return (
    <>
      <section className="section dashboard">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>Notifications</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>Notifications</span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {showMarkAllRead && (
              <button type="button" onClick={handleMarkAllRead} disabled={markingAll}
                className="btn btn-sm d-flex align-items-center gap-1 px-3" style={outlineBtn}>
                <i className="bi bi-check2-all"></i> Mark All as Read
              </button>
            )}
            {showAddButton && (
              <button type="button" onClick={openAdd} className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={primaryBtn}>
                <i className="bi bi-plus-lg"></i> Add Notification
              </button>
            )}
            <CSVLink data={csvData} headers={csvHeaders} filename="notifications.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3" style={outlineBtn}>
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        {/* Filters */}
        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-2">
                <input type="text" className="form-control" name="event_type" placeholder="Event type e.g. BOOKING_CREATED"
                  value={filters.event_type} onChange={handleFilterText} style={filterInputStyle} />
              </div>
              <div className="col-6 col-md-2">
                <Select options={REFERENCE_OPTIONS}
                  value={REFERENCE_OPTIONS.find(o => o.value === filters.reference_type) || null}
                  onChange={setFilterSelect("reference_type")} placeholder="Select Reference" isClearable
                  menuPortalTarget={document.body} styles={selectStyles} />
              </div>
              <div className="col-6 col-md-2">
                <Select options={READ_OPTIONS}
                  value={READ_OPTIONS.find(o => o.value === filters.is_read) || null}
                  onChange={setFilterSelect("is_read")} placeholder="Read / Unread" isClearable
                  menuPortalTarget={document.body} styles={selectStyles} />
              </div>
              <div className="col-6 col-md-2">
                <Select options={STATUS_FILTER_OPTIONS}
                  value={STATUS_FILTER_OPTIONS.find(o => o.value === filters.status) || null}
                  onChange={setFilterSelect("status")} placeholder="Select Status" isClearable
                  menuPortalTarget={document.body} styles={selectStyles} />
              </div>
              <div className="col-6 col-md-2">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.from || null}
                  onChange={(d) => setFilters({ ...filters, from: d ? d.format("YYYY-MM-DD") : "" })}
                  format="YYYY-MM-DD"
                  placeholder="From date"
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInputStyle, width: "100%", fontSize: 13 }}
                />
              </div>
              <div className="col-6 col-md-2">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.to || null}
                  onChange={(d) => setFilters({ ...filters, to: d ? d.format("YYYY-MM-DD") : "" })}
                  format="YYYY-MM-DD"
                  placeholder="To date"
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInputStyle, width: "100%", fontSize: 13 }}
                />
              </div>
              <div className="col-12 d-flex gap-2">
                <button type="submit" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={primaryBtn}>
                  <i className="bi bi-search"></i> Apply
                </button>
                <button type="button" onClick={handleReset} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={outlineBtn}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Stat chips */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{totalRecords}</b> Total Notifications</span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{items.length - unreadCount}</b> Read on this page</span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{unreadCount}</b> Unread on this page</span>
        </div>

        {/* Table */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div style={{ position: 'relative' }}>
            {loading && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(255,255,255,0.6)', display: 'flex',
                justifyContent: 'center', alignItems: 'center', zIndex: 10
              }}>
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", "Title", "Message", "Event Type", "Reference", "Read", "Status", "Created At", "Actions"].map((h, i) => (
                      <th key={i} style={{
                        fontSize: "11px", fontWeight: "700", color: "#475569",
                        textTransform: "uppercase", letterSpacing: "0.6px",
                        borderBottom: "2px solid #e8edf5", padding: "10px 12px",
                        whiteSpace: "nowrap", background: "#f8f9fe"
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, sno_i) => {
                    const sc = statusColors[row?.status] || { bg: "#e9ecef", color: "#495057" };
                    return (
                      <tr key={row?.id} style={!row?.is_read ? { background: "#f5f8ff" } : undefined}>
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                        <td style={{ fontSize: 13, fontWeight: row?.is_read ? 400 : 700 }}>{row?.title || "-"}</td>
                        <td style={{ fontSize: 13, minWidth: 280, whiteSpace: "normal", wordBreak: "break-word" }}>{row?.message || "-"}</td>
                        <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>{row?.event_type || "-"}</td>
                        <td style={{ fontSize: 13, textTransform: "capitalize" }}>
                          {row?.reference_type || "-"}{row?.reference_id ? ` #${row.reference_id}` : ""}
                        </td>
                        <td>
                          <span style={{
                            display: "inline-block", fontSize: 12, fontWeight: 600, padding: "2px 10px", borderRadius: 12,
                            background: row?.is_read ? "#e6f7f1" : "#fff4e6", color: row?.is_read ? "#0f8a5f" : "#e8590c"
                          }}>{row?.is_read ? "Read" : "Unread"}</span>
                        </td>
                        <td>
                          <span style={{
                            display: "inline-block", fontSize: 12, fontWeight: 600, padding: "2px 10px", borderRadius: 12,
                            background: sc.bg, color: sc.color, textTransform: "capitalize"
                          }}>{row?.status || "-"}</span>
                        </td>
                        <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>{formatDate(row?.created_at) || "-"}</td>
                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <button className="btn btn-sm d-flex align-items-center justify-content-center" title="View"
                              onClick={() => openView(row)} style={iconBtn('#e6f7f1', '#0f8a5f')}>
                              <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                            </button>
                            {showMarkRead && !row?.is_read && (
                              <button className="btn btn-sm d-flex align-items-center justify-content-center" title="Mark as read"
                                onClick={() => handleMarkRead(row)} style={{ ...iconBtn('#e7f5ff', '#1971c2'), opacity: 1 }}>
                                <i className="bi bi-check2-all" style={{ fontSize: 16, color: '#1971c2' }}></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && items.length === 0 && (
                    <tr><td colSpan={9} className="text-center text-muted py-4">No records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3" style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}>
            <div>Showing <b>{fromRecord}–{toRecord}</b> of <b>{totalRecords}</b> notifications</div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select className="form-select form-select-sm" style={{ width: 100, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
                {["10", "20", "50", "100", "500"].map(v => <option key={v} value={v}>{v}/page</option>)}
              </select>
              <span>Go</span>
              <input type="number" min="1" max={totalPage} className="form-control form-control-sm text-center" style={{ width: 60, borderRadius: 8 }}
                value={goPage} onChange={(e) => setGoPage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleGoPage()} placeholder={String(pageValue)} />
              <span>/ {totalPage}</span>
              <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>Go</button>
              <div className="d-flex align-items-center gap-1">
                <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(1)}><i className="bi bi-chevron-double-left"></i></button>
                <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(pageValue - 1)}><i className="bi bi-chevron-left"></i></button>
                {getPageNumbers().map((n, i) => n === "..."
                  ? <span key={"e" + i} className="px-1">...</span>
                  : <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => handlePagination(n)}>{n}</button>)}
                <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(pageValue + 1)}><i className="bi bi-chevron-right"></i></button>
                <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(totalPage)}><i className="bi bi-chevron-double-right"></i></button>
              </div>
            </div>
          </div>
        </div>

        {/* Add / View popup */}
        <Modal show={modal.show} onHide={closeModal} centered size={isView ? "lg" : undefined}>
          <Modal.Header closeButton closeVariant={isView ? "white" : undefined} style={isView ? { background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)", borderBottom: "none" } : undefined}>
            <div>
              <Modal.Title className="d-flex align-items-center gap-2" style={isView ? { color: "#fff", fontSize: 18, fontWeight: 600 } : { fontSize: 16, fontWeight: 700 }}>
                {isView && <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i>}
                {isView ? "Notification Details" : "Add Notification"}
              </Modal.Title>
              {!isView && <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)", marginTop: 2 }}>Add a new notification to the system</div>}
            </div>
          </Modal.Header>
          <form onSubmit={handleSubmit}>
            <Modal.Body style={isView ? { padding: 24, background: "#f8f9fe" } : undefined}>
              {modalLoading && !isView ? <ContentLoader loaderType="circleSpin" /> : isView ? (
                <DetailView fields={[
                  { label: "Title", value: detail?.title },
                  { label: "Event Type", value: detail?.event_type },
                  { label: "Message", value: detail?.message, full: true },
                  { label: "Reference Type", value: detail?.reference_type },
                  { label: "Reference ID", value: detail?.reference_id },
                  { label: "User ID", value: detail?.user_id },
                  { label: "Booking Ref", value: detail?.metadata?.booking_ref_id },
                  { label: "Transaction Ref", value: detail?.metadata?.transaction_ref },
                  { label: "Amount", value: detail?.metadata?.amount },
                  { label: "Read", value: detail ? (detail.is_read ? "Read" : "Unread") : "" },
                  { label: "Read At", value: formatDate(detail?.read_at) },
                  { label: "Status", value: detail?.status, badge: true },
                  { label: "Created At", value: formatDate(detail?.created_at) },
                ]} />
              ) : (
                <div className="row g-3">
                  <div className="col-12">
                    <div style={labelStyle}>Event Type<span className="text-danger"> *</span></div>
                    <input type="text" className="form-control" name="event_type" placeholder="e.g. BOOKING_CREATED"
                      value={form.event_type} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-12">
                    <div style={labelStyle}>Title<span className="text-danger"> *</span></div>
                    <input type="text" className="form-control" name="title" placeholder="Notification title"
                      value={form.title} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-12">
                    <div style={labelStyle}>Message<span className="text-danger"> *</span></div>
                    <textarea className="form-control" name="message" rows={3} placeholder="Notification message"
                      value={form.message} onChange={handleFormText} style={{ ...inputStyle, height: "auto" }} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>User ID<span className="text-danger"> *</span></div>
                    <input type="number" min="0" className="form-control" name="user_id" placeholder="e.g. 12"
                      value={form.user_id} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Reference Type<span className="text-danger"> *</span></div>
                    <Select options={REFERENCE_OPTIONS}
                      value={REFERENCE_OPTIONS.find(o => o.value === form.reference_type) || null}
                      onChange={(o) => setForm({ ...form, reference_type: o ? o.value : "" })}
                      placeholder="Select Reference" isClearable
                      menuPortalTarget={document.body} styles={selectStyles} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Reference ID<span className="text-danger"> *</span></div>
                    <input type="number" min="0" className="form-control" name="reference_id" placeholder="e.g. 101"
                      value={form.reference_id} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Booking ID</div>
                    <input type="text" className="form-control" name="booking_id" placeholder="e.g. BK2024001"
                      value={form.booking_id} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Amount</div>
                    <input type="number" min="0" className="form-control" name="amount" placeholder="e.g. 15450"
                      value={form.amount} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Status</div>
                    <Select options={STATUS_FILTER_OPTIONS}
                      value={STATUS_FILTER_OPTIONS.find(o => o.value === form.status) || null}
                      onChange={(o) => setForm({ ...form, status: o ? o.value : "pending" })}
                      menuPortalTarget={document.body} styles={selectStyles} />
                  </div>
                </div>
              )}
            </Modal.Body>
            {!isView && <Modal.Footer className="justify-content-end gap-2">
              <button type="button" onClick={closeModal} className="btn btn-sm px-4"
                style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 6, height: 34, fontSize: 13, color: "#444" }}>
                Cancel
              </button>
              <button type="submit" disabled={saving || modalLoading} className="btn btn-sm px-4 text-white fw-semibold"
                style={{ background: "var(--btn-bg)", border: "none", borderRadius: 6, height: 34, fontSize: 13 }}>
                Add Notification
              </button>
            </Modal.Footer>}
          </form>
        </Modal>
      </section>
    </>
  )
}

export default NotificationList;
